import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Calendar,
  Link2,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  Clock,
  Save,
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalendarPanel({ orgId, teams, userRole }) {
  const { isDark } = useTheme();
  const [feedInfo, setFeedInfo] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [timeConfig, setTimeConfig] = useState([]);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAD = ['ad', 'company_support'].includes(userRole);

  const fetchData = useCallback(async () => {
    try {
      const [feedRes, gamesRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/calendar/feed-url/${orgId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/scheduling/games/${orgId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/scheduling/time-config/${orgId}`, { withCredentials: true }),
      ]);
      setFeedInfo(feedRes.data);
      setGames(gamesRes.data || []);
      setTimeConfig(configRes.data?.time_allocations || []);
    } catch {
      // Silently handle
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCopyUrl = () => {
    if (feedInfo?.ics_url) {
      navigator.clipboard.writeText(feedInfo.ics_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAddToGoogle = async (gameId) => {
    try {
      const res = await axios.get(`${API_URL}/api/calendar/google-link/${gameId}`, { withCredentials: true });
      window.open(res.data.google_calendar_url, '_blank');
    } catch (err) {
      console.error('Failed to get google link:', err);
    }
  };

  const handleSaveTimeConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/scheduling/time-config/${orgId}`, {
        time_allocations: timeConfig,
      }, { withCredentials: true });
      setShowTimeDialog(false);
    } catch (err) {
      console.error('Failed to save time config:', err);
    }
    setSaving(false);
  };

  const addTimeAllocation = () => {
    setTimeConfig([...timeConfig, { level: '', sport: 'Basketball', duration_minutes: 90 }]);
  };

  const updateAllocation = (idx, field, value) => {
    const updated = [...timeConfig];
    updated[idx] = { ...updated[idx], [field]: field === 'duration_minutes' ? parseInt(value) || 0 : value };
    setTimeConfig(updated);
  };

  const removeAllocation = (idx) => {
    setTimeConfig(timeConfig.filter((_, i) => i !== idx));
  };

  const bg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const acceptedGames = games.filter(g => g.status === 'accepted');

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="calendar-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-emerald-500" />
          <h2 className={`text-lg font-semibold ${text}`}>Calendar Integration</h2>
        </div>
        {isAD && (
          <Button size="sm" variant="outline" onClick={() => setShowTimeDialog(true)}
            className={isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : ''}
            data-testid="time-config-btn"
          >
            <Clock className="h-4 w-4 mr-1" /> Time Allocation
          </Button>
        )}
      </div>

      {/* Subscription Card */}
      <Card className={bg}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-sm font-medium ${muted}`}>Calendar Subscription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className={`text-sm ${muted}`}>Subscribe to your schedule in Google Calendar, Apple Calendar, or Outlook.</p>
          {feedInfo && (
            <div className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
              <Link2 className={`h-4 w-4 flex-shrink-0 ${muted}`} />
              <code className={`text-xs flex-1 truncate ${text}`} data-testid="ics-url">{feedInfo.ics_url}</code>
              <Button size="sm" variant="ghost" onClick={handleCopyUrl} data-testid="copy-url-btn">
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          )}
          <div className="flex gap-2">
            {feedInfo?.google_subscribe_url && (
              <Button size="sm" variant="outline" onClick={() => window.open(feedInfo.google_subscribe_url, '_blank')}
                className={isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : ''}
                data-testid="subscribe-google-btn"
              >
                <ExternalLink className="h-4 w-4 mr-1" /> Add to Google Calendar
              </Button>
            )}
            {feedInfo?.ics_url && (
              <Button size="sm" variant="outline" onClick={() => window.open(`${API_URL}/api/calendar/feed/${orgId}`, '_blank')}
                className={isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : ''}
                data-testid="download-ics-btn"
              >
                <Calendar className="h-4 w-4 mr-1" /> Download .ics
              </Button>
            )}
          </div>
          {/* Clarifying footnote (Session 19, May 12 2026) — operators were
              concerned that "emergent" in the preview-hosted URL meant the
              feed depended on a third-party SaaS. It does not: the .ics is
              served by OL's own FastAPI backend at whatever domain is set
              in REACT_APP_BACKEND_URL. */}
          <p className={`text-[11px] leading-relaxed pt-1 ${muted}`} data-testid="calendar-native-note">
            Standard iCalendar (.ics) feed served from your OmniLeagues
            backend — no third-party services required. The URL above
            reflects the current hosting domain
            (<code className="text-[10px]">REACT_APP_BACKEND_URL</code>)
            and automatically updates when you deploy to your own
            production servers.
          </p>
        </CardContent>
      </Card>

      {/* Upcoming Games with Add to Calendar */}
      {acceptedGames.length > 0 && (
        <Card className={bg}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm font-medium ${muted}`}>Upcoming Games</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {acceptedGames.slice(0, 10).map(g => (
                <div key={g.id} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                  <div>
                    <p className={`text-sm font-medium ${text}`}>
                      {g.team_name || 'Team'} vs {g.opponent?.team_name || 'Opponent'}
                    </p>
                    <p className={`text-xs ${muted}`}>
                      {g.game_datetime?.substring(0, 10)} at {g.game_datetime?.substring(11, 16)}
                      {g.venue_name && ` — ${g.venue_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={g.is_home ? 'default' : 'secondary'} className="text-xs">
                      {g.is_home ? 'Home' : 'Away'}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => handleAddToGoogle(g.id)}
                      className={muted} title="Add to Google Calendar"
                      data-testid={`gcal-btn-${g.id}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Allocation Config Dialog (AD only) */}
      <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={text}>Time Allocation per Level</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="time-config-dialog">
            <p className={`text-sm ${muted}`}>Configure game block duration per sport/level for scheduling conflicts.</p>
            {timeConfig.map((ta, idx) => (
              <div key={idx} className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className={`text-xs ${muted}`}>Level</Label>
                  <Input
                    value={ta.level}
                    onChange={e => updateAllocation(idx, 'level', e.target.value)}
                    placeholder="MS, JV, Varsity..."
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  />
                </div>
                <div className="flex-1">
                  <Label className={`text-xs ${muted}`}>Sport</Label>
                  <Input
                    value={ta.sport}
                    onChange={e => updateAllocation(idx, 'sport', e.target.value)}
                    placeholder="Basketball"
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  />
                </div>
                <div className="w-24">
                  <Label className={`text-xs ${muted}`}>Minutes</Label>
                  <Input
                    type="number" min="30" max="240"
                    value={ta.duration_minutes}
                    onChange={e => updateAllocation(idx, 'duration_minutes', e.target.value)}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  />
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeAllocation(idx)} className="text-red-400 hover:text-red-300 mb-0.5">X</Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addTimeAllocation}
              className={isDark ? 'border-slate-600 text-slate-300' : ''}
            >
              + Add Level
            </Button>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowTimeDialog(false)} className={muted}>Cancel</Button>
              <Button onClick={handleSaveTimeConfig} disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-time-config-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
